/** @format */

import React from "react";
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { Blog } from "../types";
import { formatDate } from "../utils/formatUtils";

interface BlogCardProps {
  blog: Blog;
  onClick: () => void;
}

const BlogCard: React.FC<BlogCardProps> = ({ blog, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-100 hover:border-blue-200 transform hover:-translate-y-1"
    >
      {/* Blog Image */}
      <div className="aspect-video bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden relative">
        {blog.link ? (
          <img
            src={blog.link}
            alt={blog.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
            <div className="text-center">
              <div className="text-5xl mb-3 opacity-60">📝</div>
              <div className="text-gray-500 text-sm font-medium">No Image</div>
            </div>
          </div>
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Read More Arrow */}
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
          <ArrowRightIcon className="h-4 w-4 text-blue-600" />
        </div>
      </div>

      {/* Blog Content */}
      <div className="p-6">
        {/* Categories */}
        {blog.categories && blog.categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {blog.categories.slice(0, 2).map((category, index) => (
              <span
                key={index}
                className="px-3 py-1 text-xs font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full shadow-sm"
              >
                {category}
              </span>
            ))}
            {blog.categories.length > 2 && (
              <span className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                +{blog.categories.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors duration-300 leading-tight">
          {blog.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 mb-5 line-clamp-3 leading-relaxed">
          {blog.description}
        </p>

        {/* Meta Information */}
        <div className="space-y-3">
          {/* Author and Date */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-3">
              {/* Author */}
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <UserIcon className="h-3 w-3 text-white" />
                </div>
                <span className="text-gray-700 font-medium">
                  {blog.author.includes("@")
                    ? blog.author.split("@")[0]
                    : blog.author}
                </span>
              </div>

              {/* Date */}
              <div className="flex items-center space-x-2 text-gray-500">
                <CalendarIcon className="h-3 w-3" />
                <span>{formatDate(blog.createdAt)}</span>
              </div>
            </div>

            {/* Reading Time */}
            <div className="flex items-center space-x-1 bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
              <ClockIcon className="h-3 w-3" />
              <span>{blog.readingTime} min</span>
            </div>
          </div>

          {/* Word Count and Tags */}
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
              {blog.wordCount} words
            </div>

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex gap-1">
                {blog.tags.slice(0, 2).map((tag, index) => (
                  <span
                    key={index}
                    className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
                {blog.tags.length > 2 && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                    +{blog.tags.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Hover Effect Line */}
        <div className="mt-4 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
      </div>
    </div>
  );
};

export default BlogCard;
